import { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';

const SESSION_KEY = 'game-session';

export default function useSocket({ roomCode, playerName, gameState, onRejoinSuccess, onRejoinFailure }) {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [retryCount, setRetryCount] = useState(0);

  // Refs to avoid stale closures in socket handlers
  const roomCodeRef = useRef('');
  const playerNameRef = useRef('');
  const gameStateRef = useRef('join');
  const persistentIdRef = useRef('');

  // Keep refs in sync
  useEffect(() => { roomCodeRef.current = roomCode; }, [roomCode]);
  useEffect(() => { playerNameRef.current = playerName; }, [playerName]);
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);

  // Stable callback refs so the socket init effect runs only once
  const onRejoinSuccessRef = useRef(onRejoinSuccess);
  const onRejoinFailureRef = useRef(onRejoinFailure);
  useEffect(() => { onRejoinSuccessRef.current = onRejoinSuccess; }, [onRejoinSuccess]);
  useEffect(() => { onRejoinFailureRef.current = onRejoinFailure; }, [onRejoinFailure]);

  const saveSession = (rc, pn, pid) => {
    persistentIdRef.current = pid;
    localStorage.setItem(SESSION_KEY, JSON.stringify({ roomCode: rc, playerName: pn, persistentId: pid }));
  };

  const clearSession = () => {
    persistentIdRef.current = '';
    localStorage.removeItem(SESSION_KEY);
  };

  // Initialize socket connection (runs once)
  useEffect(() => {
    console.log('Creating socket connection to', process.env.REACT_APP_SERVER_URL);
    const newSocket = io(process.env.REACT_APP_SERVER_URL, {
      transports: ['polling', 'websocket'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Socket connected successfully');
      setConnectionStatus('connected');
      setRetryCount(0);

      // Auto-rejoin if we were in a game
      let rc = roomCodeRef.current;
      let pn = playerNameRef.current;
      let pid = persistentIdRef.current;
      const gs = gameStateRef.current;

      if ((!rc || !pn) && gs === 'join') {
        try {
          const saved = JSON.parse(localStorage.getItem(SESSION_KEY));
          if (saved?.roomCode && saved?.playerName && saved?.persistentId) {
            rc = saved.roomCode;
            pn = saved.playerName;
            pid = saved.persistentId;
          }
        } catch (e) { /* ignore */ }
      }

      if (rc && pn) {
        console.log(`Auto-rejoining room ${rc} as ${pn}...`);
        newSocket.emit('rejoin-room', { roomCode: rc, playerName: pn, persistentId: pid }, (response) => {
          if (response.success) {
            console.log('Rejoin successful, syncing state');
            if (response.persistentId) {
              persistentIdRef.current = response.persistentId;
              localStorage.setItem(SESSION_KEY, JSON.stringify({
                roomCode: rc, playerName: pn, persistentId: response.persistentId,
              }));
            }
            onRejoinSuccessRef.current(rc, pn, response);
          } else {
            console.log('Rejoin failed:', response.message);
            persistentIdRef.current = '';
            localStorage.removeItem(SESSION_KEY);
            onRejoinFailureRef.current();
          }
        });
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      if (reason !== 'io client disconnect') {
        setConnectionStatus('disconnected');
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setConnectionStatus('error');
    });

    newSocket.io.on('reconnect_attempt', (attempt) => {
      setRetryCount(attempt);
      setConnectionStatus('connecting');
    });

    newSocket.io.on('reconnect_failed', () => {
      setConnectionStatus('failed');
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { socket, connectionStatus, retryCount, persistentIdRef, saveSession, clearSession };
}
